import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { LiveTaskFeed } from './LiveTaskFeed';
import { NodeNetwork } from './NodeNetwork';
type Mode = 'earn' | 'hire';
const copy: Record<Mode, {
  body: string;
  primary: string;
  secondary: string;
}> = {
  earn: {
    body: 'Grab quick paid gigs in your city. Test an app, check a store, make a call. $5 to $20 a pop, cashed out in 24h.',
    primary: 'Find gigs near me',
    secondary: 'How payouts work'
  },
  hire: {
    body: 'One API call puts a verified local on the job in 150+ countries. Photos, receipts, call logs back in your inbox, usually within the hour.',
    primary: 'Get an API key',
    secondary: 'Read the docs'
  }
};
const ease = [0.23, 1, 0.32, 1] as const;
export function Hero() {
  const [mode, setMode] = useState<Mode>('earn');
  const reduce = useReducedMotion();
  const current = copy[mode];
  const enter = (delay: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      ease,
      delay
    }
  };
  return <section className="mx-auto grid max-w-6xl gap-14 px-6 pb-24 pt-14 md:pt-20 lg:grid-cols-[1fr_1.05fr] lg:gap-12" data-id="element-61" data-mp="3o5j1b4tkf9j6">
      <div className="flex flex-col justify-center" data-id="element-62" data-mp="3e9fuhlnrqmpw">
        <motion.div {...enter(0)} data-id="element-63" data-mp="3e7uydrlt2vpx">
          <div role="tablist" aria-label="Choose your path" className="mb-8 inline-flex w-fit rounded-full border border-line bg-surface p-1" data-id="element-64" data-mp="30i18r72h87f5">
            {(['earn', 'hire'] as Mode[]).map(m => <button key={m} role="tab" aria-selected={mode === m} onClick={() => setMode(m)} className={`relative whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-150 ${mode === m ? 'text-canvas' : 'text-muted hover:text-ink'}`} data-id="element-65" data-mp="2qjv8sv08dbcx">
                {mode === m && <motion.span layoutId="hero-mode" className="absolute inset-0 rounded-full bg-ink" transition={{
              duration: 0.22,
              ease
            }} data-id="element-66" data-mp="s3349bcaykwh" />}
                <span className="relative" data-id="element-67" data-mp="ic86patlvyvq">{m === 'earn' ? 'I wanna earn' : "I'm building"}</span>
              </button>)}
          </div>
        </motion.div>

        <h1 className="font-display text-5xl font-semibold leading-[1.02] tracking-tight md:text-6xl lg:text-7xl" data-id="element-68" data-mp="deyj80ps1xaj">
          {['Deploy', 'real', 'humans', 'to'].map((word, i) => <motion.span key={word} className="mr-[0.22em] inline-block" {...enter(0.05 + i * 0.04)} data-id="element-69" data-mp="163436l90bi9b" data-mpi={i}>
              {word}
            </motion.span>)}
          <motion.span className="inline-block text-accent" {...enter(0.25)} data-id="element-70" data-mp="x1nuqj1r5q7q">
            any city.
          </motion.span>
        </h1>

        <motion.div className="mt-6 min-h-[5.5rem] max-w-lg" {...enter(0.3)} data-id="element-71" data-mp="3ltqghnsxvfys">
          <AnimatePresence mode="wait" data-id="element-72" data-mp="3hr3q7rbx233p">
            <motion.p key={mode} initial={{
            opacity: 0,
            y: 4,
            filter: 'blur(4px)'
          }} animate={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} exit={{
            opacity: 0,
            y: -4,
            filter: 'blur(4px)'
          }} transition={{
            duration: 0.18,
            ease: 'easeOut'
          }} className="text-lg leading-relaxed text-muted" data-id="element-73" data-mp="3htg8k5ll6fha">
              {current.body}
            </motion.p>
          </AnimatePresence>
        </motion.div>

        <motion.div id="start" className="mt-8 flex flex-wrap items-center gap-3" {...enter(0.36)} data-id="element-74" data-mp="2fzanp4xzbi70">
          <a href="#briefs" className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent px-6 py-3 font-medium text-accent-ink transition-transform duration-150 ease-out active:scale-[0.97]" data-id="element-75" data-mp="2kxc8bub1zpkl">
            <AnimatePresence mode="wait" initial={false} data-id="element-76" data-mp="umzhtubym26t">
              <motion.span key={current.primary} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.14
            }} data-id="element-77" data-mp="uokcrl26rn57">
                {current.primary}
              </motion.span>
            </AnimatePresence>
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-78" data-mp="3vgniwwf2k3sy" />
          </a>
          <a href="#how" className="whitespace-nowrap rounded-full border border-line bg-surface px-6 py-3 font-mono text-sm font-medium transition-colors duration-150 hover:border-ink/40" data-id="element-79" data-mp="e77gdj6c13fw">
            {current.secondary}
          </a>
        </motion.div>
      </div>

      <motion.div className="flex flex-col" initial={reduce ? false : {
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.3,
      ease,
      delay: 0.15
    }} data-id="element-80" data-mp="3rg3jfm70wo3n">
        <NodeNetwork data-id="element-81" data-mp="2yz9fig09nbot" />
        <div className="relative z-10 mx-3 -mt-14 lg:-mt-24 lg:ml-12 lg:mr-0" data-id="element-82" data-mp="2yyhrbq900g8k">
          <LiveTaskFeed data-id="element-83" data-mp="2u1hm1hjz3hii" />
        </div>
      </motion.div>
    </section>;
}